import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { Button, notification, Form, Input, Typography, Card, Divider } from 'antd'
import { callApi } from '../../../../context/api'
import Conversations from '@twilio/conversations'
import { SendOutlined } from '@ant-design/icons'
import { Conversation } from '@twilio/conversations/lib/conversation'
import SpinLoader from '../../../helpers/SpinLoader'
import { Message } from '@twilio/conversations/lib/message'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useCallContext } from '../../../../context/call/CallProvider'

import './RoomChat.less'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons'

const { Title } = Typography

type TChatPagination = {
	page: number
	count: number
}

type TProps = {
	onClose: () => void
	onNewMessage?: (msg?: Message) => void
}

const RoomChat: FunctionComponent<TProps> = ({ onClose, onNewMessage }) => {
	const [sendMessageForm] = Form.useForm()
	const { call } = useCallContext()
	const [loading, setLoading] = useState<boolean>(false)
	const [twilioConversation, setTwilioConversation] = useState<Conversation>(null)
	const [messages, setMessages] = useState<Message[]>([])
	const [identity, setIdentity] = useState<string>()
	const [pagination, setPagination] = useState<TChatPagination>({
		page: 1,
		count: 0,
	})

	const chatEndMessagesRef = useRef<HTMLDivElement>()
	const chatMessagesRef = useRef<HTMLDivElement>()
	const textAreaRef = useRef<any>()
	const messageSize = 30

	useEffect(() => {
		setLoading(true)
		getChat()
			.then((response) => {
				setIdentity(response.identity)
				return connectConversation(response.twilioToken, response.conversationSid)
			})
			.catch((e) => {
				notification.error({ message: 'Error', description: e.message })
			})
			.finally(() => {
				setLoading(false)
			})
	}, [call.call])

	useEffect(() => {
		const element = chatMessagesRef?.current
		if (element.scrollHeight > element.clientHeight) {
			element.style.flexDirection = 'column-reverse'
		} else {
			element.style.flexDirection = 'column'
		}
	}, [messages?.length])

	const connectConversation = async (token: string, conversationSid: string) => {
		if (!token || !conversationSid) {
			setTwilioConversation(null)
			return
		}

		const conversationsClient: Conversations = await Conversations.create(token)
		const conversation = await conversationsClient.getConversationBySid(conversationSid)
		if (conversation) {
			conversation.on('messageAdded', addMessage)
			const messagesCount = await conversation.getMessagesCount()
			const messages = await conversation.getMessages(messageSize, messagesCount)
			setTwilioConversation(conversation)
			setMessages(messages.items)
			setPagination((pagination) => ({
				...pagination,
				count: messagesCount,
			}))
		}
	}

	const loadMoreMessages = async () => {
		const messageAnchor = pagination.count - pagination.page * messageSize - 1
		if (messageAnchor >= 0) {
			const newMessages = await twilioConversation?.getMessages(messageSize, messageAnchor)
			setMessages((messages) => newMessages.items.concat(messages))
			setPagination((pagination) => ({
				...pagination,
				page: pagination.page + 1,
			}))
		}
	}

	const addMessage = (msg: Message) => {
		setMessages((messages) => messages.concat(msg))
		textAreaRef?.current?.focus()
		chatEndMessagesRef?.current?.scrollIntoView({ behavior: 'smooth' })
		onNewMessage(msg)
	}

	const getChat = () => {
		return callApi(`/call/${call.call.id}/chat`, {
			method: 'POST',
			body: {
				firstName: call.participant?.firstName,
				lastName: call.participant?.lastName,
			},
		}).then((response) => {
			if (!response.ok) {
				throw new Error(`Cannot connect to chat`)
			}
			return response.payload?.data
		})
	}

	const onPressEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (!e.shiftKey) {
			sendMessageForm.submit()
		}
	}

	const onSendMessage = async ({ message }: { message: string }) => {
		if (!message) return

		sendMessageForm.resetFields()
		await twilioConversation?.sendMessage(message)
	}
	return (
		<section className="room-chat">
			<Card>
				<SpinLoader loading={!!loading}>
					<div className="room-chat-header">
						<Title level={5} className="chat-title">
							Meeting Chat
						</Title>
						<Button
							type="link"
							onClick={onClose}
							shape="circle"
							size="middle"
							icon={<FontAwesomeIcon size="lg" icon={faTimesCircle} color="#8C8C8C" />}
						/>
					</div>
					<Divider className="chat-divider" />
					<div className="chat-messages" id="chatMessages" ref={chatMessagesRef}>
						{!!messages?.length && (
							<InfiniteScroll
								dataLength={messages.length}
								next={loadMoreMessages}
								inverse={true}
								hasMore={true}
								loader={<></>}
								scrollableTarget="chatMessages"
							>
								{messages?.map((msg: Message) => (
									<div key={msg.sid} className="message-item">
										<div className="message-header">
											<span className="author">{identity === msg.author ? 'You' : msg.author}</span>
											<span className="time">{moment(msg.dateCreated).format('HH:mm')}</span>
										</div>
										<span className="message-body">{msg.body}</span>
									</div>
								))}
								<div ref={chatEndMessagesRef} />
							</InfiniteScroll>
						)}
					</div>
					<div className="chat-form">
						<Divider className="chat-divider" />
						<Form form={sendMessageForm} onFinish={onSendMessage}>
							<Form.Item name="message" className="chat-text-input-wrapper">
								<Input.TextArea
									ref={textAreaRef}
									placeholder="Start typing..."
									className="chat-text-input"
									onPressEnter={onPressEnter}
								/>
							</Form.Item>
							<span className="hint">For new line use Shift+Enter</span>
							<Button className="btn-send" size="large" type="text" htmlType="submit" icon={<SendOutlined />} />
						</Form>
					</div>
				</SpinLoader>
			</Card>
		</section>
	)
}

export default RoomChat
